/**
 * National production incentives, by country.
 *
 * A COPY, AND IT SAYS SO — A SNAPSHOT, NOT A LIVE VIEW. Since 2026-10 the source
 * of record is the `film_incentive_program` table (admin-edited; 42 national
 * schemes compiled July 2026 from the "Europe Incentives — Final Review"
 * verification sheet, cross-checked against the Global Incentives Index 2026
 * (Olsberg•SPI) and official national sources). This service holds no grant on
 * that table, so this narrowed copy is pinned to the table's frozen import
 * snapshot, `database/migrations/data/film-incentive-programs-2026-10.json`,
 * and `backend/src/__tests__/incentive-parity.test.ts` reconciles it field by
 * field. An administrator's later edit does NOT reach this file; reading the
 * table through `breakdown_svc` is the recorded follow-up
 * (docs/features/film-incentives.md).
 *
 * THESE ARE SOURCED FIGURES, NOT MODELLED ONES — a distinction worth keeping,
 * because the neighbouring dataset is the opposite. The twelve 0–100 city
 * metrics in `frontend-client/app/Support/EuropeTopCities.php` include an
 * "Incentives & Rebates" score whose own copy says "(modelled)"; that one is
 * editorial and must never drive a filter or a recommendation.
 *
 * THE DISCLAIMER TRAVELS WITH THE NUMBERS. Incentive terms change frequently
 * and without notice, and a producer budgeting against a stale rate has a real
 * problem. Every surface that shows one shows `INCENTIVE_DISCLAIMER` beside it.
 */
export interface CountryIncentive {
  readonly iso: string;
  readonly name: string;
  /** The headline rate, for ranking and display. */
  readonly headlineRate: number;
  /** As written — may be a range or an "up to". Prefer this over the number. */
  readonly label: string;
  /** Achievable with bonuses, where one is stated. */
  readonly maxRate: number | null;
  readonly kind: string;
}

export const INCENTIVE_COMPILED = '2026-07';

export const INCENTIVE_DISCLAIMER =
  'Compiled July 2026. Incentive terms change frequently — confirm current rules with each national film body before budgeting.';

export const COUNTRY_INCENTIVES: readonly CountryIncentive[] = Object.freeze([
  { iso: 'AL', name: "Albania", headlineRate: 15, label: "Up to 15%", maxRate: 15, kind: "Selective grant" },
  { iso: 'AM', name: "Armenia", headlineRate: 25, label: "25%", maxRate: 35, kind: "Rebate" },
  { iso: 'AT', name: "Austria", headlineRate: 30, label: "Up to 30%", maxRate: 35, kind: "Rebate" },
  { iso: 'BA', name: "Bosnia and Herzegovina", headlineRate: 30, label: "30%", maxRate: 30, kind: "Rebate" },
  { iso: 'BE', name: "Belgium", headlineRate: 38, label: "~38–40%", maxRate: 40, kind: "Tax shelter" },
  { iso: 'BG', name: "Bulgaria", headlineRate: 25, label: "25%", maxRate: 25, kind: "Rebate" },
  { iso: 'CH', name: "Switzerland", headlineRate: 20, label: "20%", maxRate: 50, kind: "Selective grant" },
  { iso: 'CY', name: "Cyprus", headlineRate: 45, label: "Up to 45%", maxRate: 75, kind: "Rebate" },
  { iso: 'CZ', name: "Czechia", headlineRate: 25, label: "25%", maxRate: 35, kind: "Rebate" },
  { iso: 'DE', name: "Germany", headlineRate: 30, label: "30%", maxRate: 30, kind: "Rebate" },
  { iso: 'DK', name: "Denmark", headlineRate: 25, label: "25%", maxRate: 25, kind: "Rebate" },
  { iso: 'EE', name: "Estonia", headlineRate: 30, label: "30% or 40%", maxRate: 40, kind: "Rebate" },
  { iso: 'ES', name: "Spain", headlineRate: 30, label: "30% / 25%", maxRate: 60, kind: "Tax credit" },
  { iso: 'FI', name: "Finland", headlineRate: 25, label: "25%", maxRate: 40, kind: "Rebate" },
  { iso: 'FR', name: "France", headlineRate: 30, label: "30%", maxRate: 40, kind: "Rebate" },
  { iso: 'GB', name: "United Kingdom", headlineRate: 25.5, label: "25.5% net", maxRate: 39.75, kind: "Tax credit" },
  { iso: 'GE', name: "Georgia", headlineRate: 20, label: "20%", maxRate: 25, kind: "Rebate" },
  { iso: 'GR', name: "Greece", headlineRate: 40, label: "40%", maxRate: 40, kind: "Rebate" },
  { iso: 'HR', name: "Croatia", headlineRate: 25, label: "25%", maxRate: 30, kind: "Rebate" },
  { iso: 'HU', name: "Hungary", headlineRate: 30, label: "30%", maxRate: 37.5, kind: "Rebate" },
  { iso: 'IE', name: "Ireland", headlineRate: 32, label: "32%", maxRate: 40, kind: "Tax credit" },
  { iso: 'IS', name: "Iceland", headlineRate: 25, label: "25–35%", maxRate: 35, kind: "Rebate" },
  { iso: 'IT', name: "Italy", headlineRate: 40, label: "40%", maxRate: 40, kind: "Tax credit" },
  { iso: 'LT', name: "Lithuania", headlineRate: 30, label: "Up to 30%", maxRate: 30, kind: "Tax shelter" },
  { iso: 'LU', name: "Luxembourg", headlineRate: 0, label: "Selective", maxRate: 0, kind: "Selective grant" },
  { iso: 'LV', name: "Latvia", headlineRate: 30, label: "30%", maxRate: 50, kind: "Rebate" },
  { iso: 'MD', name: "Moldova", headlineRate: 25, label: "25%", maxRate: 30, kind: "Rebate" },
  { iso: 'ME', name: "Montenegro", headlineRate: 30, label: "30%", maxRate: 35, kind: "Rebate" },
  { iso: 'MK', name: "North Macedonia", headlineRate: 20, label: "20%", maxRate: 20, kind: "Rebate" },
  { iso: 'MT', name: "Malta", headlineRate: 30, label: "30%", maxRate: 40, kind: "Rebate" },
  { iso: 'NL', name: "Netherlands", headlineRate: 35, label: "35%", maxRate: 35, kind: "Rebate" },
  { iso: 'NO', name: "Norway", headlineRate: 25, label: "25%", maxRate: 25, kind: "Rebate" },
  { iso: 'PL', name: "Poland", headlineRate: 30, label: "30%", maxRate: 30, kind: "Rebate" },
  { iso: 'PT', name: "Portugal", headlineRate: 30, label: "30%", maxRate: 40, kind: "Rebate" },
  { iso: 'RO', name: "Romania", headlineRate: 30, label: "30%", maxRate: 30, kind: "Rebate" },
  { iso: 'RS', name: "Serbia", headlineRate: 25, label: "25%", maxRate: 30, kind: "Rebate" },
  { iso: 'SE', name: "Sweden", headlineRate: 25, label: "Up to 25%", maxRate: 25, kind: "Rebate" },
  { iso: 'SI', name: "Slovenia", headlineRate: 25, label: "25%", maxRate: 25, kind: "Rebate" },
  { iso: 'SK', name: "Slovakia", headlineRate: 33, label: "33%", maxRate: 33, kind: "Rebate" },
  { iso: 'TR', name: "Turkey", headlineRate: 30, label: "Up to 30%", maxRate: 30, kind: "Rebate" },
  { iso: 'UA', name: "Ukraine", headlineRate: 30, label: "30%", maxRate: 30, kind: "Rebate" },
  { iso: 'XK', name: "Kosovo", headlineRate: 30, label: "30%", maxRate: 30, kind: "Rebate" },]);

const BY_ISO = new Map(COUNTRY_INCENTIVES.map((c) => [c.iso, c]));

/** Null when we hold no scheme for a country — never a zero, which would read
 *  as "no incentive" rather than "we do not know". */
export function incentiveFor(iso: string | null | undefined): CountryIncentive | null {
  if (iso === null || iso === undefined) return null;
  return BY_ISO.get(iso.toUpperCase()) ?? null;
}
